import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Flex,
  Select,
  Input,
  Button,
  Text,
  ModalCloseButton,
  Checkbox,
} from '@chakra-ui/react';
import { useToasts } from 'react-toast-notifications';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { put, createTag } from '../api/tag';
import { tagType, unitOptions } from '../utils/constants';
import { GetAllSides } from '../api/side';

const functionOptions = ['RW', 'RO', 'VirtualTag'];

const TagModalComponent = ({
  isOpen,
  onClose,
  tag,
  deviceOptions,
  onSuccess,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm();

  const { addToast } = useToasts();

  const [allDevices, setAllDevices] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchDevices = async () => {
      try {
        const data = await GetAllSides();
        setAllDevices(data);
      } catch (err) {
        console.error('Error fetching devices:', err);
        setAllDevices([]);
      }
    };
    fetchDevices();
  }, [isOpen]);

  const selectedDeviceId = useWatch({ control, name: 'device' });
  const selectedDevice = allDevices.find(d => d.id === selectedDeviceId)
    || deviceOptions.find(d => d.id === selectedDeviceId);
  const isVirtualDevice = selectedDevice?.connectionType === 'VirtualDevice'
    || selectedDevice?.connType === 'VirtualDevice';

  useEffect(() => {
    if (tag) {
      setValue('name', tag.name || '');
      setValue('device', tag.deviceId || '');
      setValue('type', tag.type || '');
      setValue('address', tag.address ?? '');
      setValue('unit', tag.unit || '7');
      setValue(
        'function',
        tag.function ||
          (selectedDevice?.connectionType === 'VirtualDevice'
            ? 'VirtualTag'
            : '')
      );
      setValue('homePage', tag.homePage || false);
    } else {
      setValue('name', '');
      setValue('device', '');
      setValue('type', '');
      setValue('address', '');
      setValue('unit', '7');
      setValue('function', '');
      setValue('homePage', false);
    }
  }, [tag, setValue, isOpen]);

  const onSubmit = async data => {
    try {
      const payload = {
        Name: data.name,
        enabled: true,
        type: data.type,
        address: isVirtualDevice ? 0 : Number(data.address),
        unit: data.unit,
        function: isVirtualDevice ? 'VirtualTag' : data.function,
        homePage: data.homePage,
        deviceId: data.device,
      };

      if (tag && tag.registerId) {
        await put(tag.registerId, payload);
      } else {
        await createTag(payload);
      }

      addToast('Tag updated successfully', { appearance: 'success' });
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating tag:', error);
      addToast('Error updating tag', { appearance: 'error' });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{tag ? 'Edit Tag' : 'Add New Tag'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex direction="column" gap="4">
              {/* Name */}
              <Flex align="center">
                <Text minWidth="120px">Name:</Text>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      placeholder="Tag Name"
                      variant="flushed"
                    />
                  )}
                />
              </Flex>
              {errors.name && <Text color="red">{errors.name.message}</Text>}

              {/* Device */}
              <Flex align="center">
                <Text minWidth="120px">Device Name:</Text>
                <Controller
                  name="device"
                  control={control}
                  rules={{ required: 'Device is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Device"
                      variant="flushed"
                    >
                      {allDevices.map(device => (
                        <option key={device.id} value={device.id}>
                          {device.deviceName}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </Flex>

              {/* Type */}
              <Flex align="center">
                <Text minWidth="120px">Type:</Text>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: 'Type is required' }}
                  render={({ field }) => (
                    <Select
                      {...field}
                      placeholder="Select Type"
                      variant="flushed"
                      defaultValue={tag ? tag.type : ''}
                    >
                      {tagType.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </Flex>

              {/* Address - only if NOT VirtualDevice */}
              {!isVirtualDevice && (
                <Flex align="center">
                  <Text minWidth="120px">Address:</Text>
                  <Controller
                    name="address"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        placeholder="Address"
                        variant="flushed"
                      />
                    )}
                  />
                </Flex>
              )}

              {/* Unit */}
              <Flex align="center">
                <Text minWidth="120px">Unit:</Text>
                <Controller
                  name="unit"
                  control={control}
                  rules={{ required: 'Unit is required' }}
                  render={({ field }) => (
                    <Select {...field} variant="flushed">
                      {unitOptions.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  )}
                />
              </Flex>

              {/* Function - only if NOT VirtualDevice */}
              {!isVirtualDevice && (
                <Flex align="center">
                  <Text minWidth="120px">Function:</Text>
                  <Controller
                    name="function"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder="Select Function"
                        variant="flushed"
                      >
                        {functionOptions.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </Select>
                    )}
                  />
                </Flex>
              )}

              {/* HomePage */}
              <Flex align="center">
                <Controller
                  name="homePage"
                  control={control}
                  render={({ field }) => (
                    <Checkbox {...field} isChecked={field.value}>
                      Home Page
                    </Checkbox>
                  )}
                />
              </Flex>
            </Flex>

            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={onClose}>
                Close
              </Button>
              <Button colorScheme="green" type="submit">
                {tag ? 'Save Changes' : 'Save'}
              </Button>
            </ModalFooter>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TagModalComponent;
